import { useState, useRef, useEffect } from "react";
import { Alert, Row, Col, FormGroup, Label, Input, Table, Button } from "reactstrap";
import { TruncateText } from "../../helper/Utilities";
import { useLibraryContentsVideosPaginated } from "../../hooks/useLibraryContents";
import { LibraryContentVideoPaged } from "../../interfaces/contentManagement/ContentLibraryPaginated";
import Loading from "../Loading";
import PaginationSet from "../Shared/Forms/PaginationSet";
import ToggleableImage from "../Shared/ToggleableImage";

interface FilterParams { 
    search : string;
    source : number;
    page : number;
}

interface Props {
    limit : number;
    haveActions? : boolean;
    select? : (id : string) => void;
    selectedValue? : string;
}

export default function VideoLibraryTable ({ limit = 10, haveActions = false, select, selectedValue } : Props) {
    const Sources = {
        NUDGE : 0,
        MIC : 1,
        MC : 2,
        OTHERS : 4
    }

    const sourcesOptions = [
        "Nudges",
        "myintention.coach",
        "movement.coach",
        "Others"
    ]

    const [rowLength, setRowLength] = useState<number>(3);

    const [libraryContent, setLibraryContent] = useState<LibraryContentVideoPaged>({
        total_pages : 0,
        page : 1,
        docs : []
    });

    const [filter, setFilter] = useState<FilterParams> ({
        search : "",
        source : Sources.NUDGE,
        page : 1,
    });
    const [refetching, setRefetching] = useState<boolean>(false);
    let timer = useRef<number | null> (null);

    const { data, loading, error , refetch} = useLibraryContentsVideosPaginated("", Sources.NUDGE, 1, limit);

    const FilterLibaryContents = (search : string, source : number) => {

        setRefetching(true);
            if(timer?.current) {
            clearTimeout(timer.current);
            timer.current = null;
        }

        timer.current = window.setTimeout(async () => {
            setFilter((prev) => ({...prev, page : 1}));
            await refetch({
                search : search,
                source : source,
                page : 1,
                limit : limit
            });
            setRefetching(false);
        },1500);

    }

    const OnPageChange = async (page : number) => {
        
        if(refetching || page === filter.page) { return; }
        setFilter((prev) => ({...prev, page : page}));
        setRefetching(true);
        await refetch({
            search : filter.search,
            source : filter.source,
            page : page,
            limit : limit
        });
        setRefetching(false);
    }
    const SelectVideo = (id : string) => {
        select(id);
    }

    useEffect(() => {
        if(data) {
            setLibraryContent(data.library_content_videos);
        }
    },[data]);
    
    useEffect(() => {
        setRowLength(haveActions ? 3 : 2);
    },[haveActions]);

    return (
        <>

            {
                error &&
                <Alert color="danger">
                    Oops, we’re having trouble getting the Library Contents. Please give it a minute, and if it doesn’t resolve, let us know.
                </Alert>
            }

            <Row>
                <Col md={6}>
                    <FormGroup className="d-flex align-items-center">
                        <Label className="mr-2">Search: </Label>
                        <Input type="text" 
                            placeholder="Search by Name or Description"
                            value={filter.search} 
                            onChange={(e) => { setFilter((prev) => ({...prev, search : e.target.value}) )}} 
                            onKeyUp={(e) => { FilterLibaryContents(e.currentTarget.value,  filter.source) }}
                        />
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup className="d-flex align-items-center">
                        <Label className="mr-2">Sources: </Label>
                        <Input type="select" 
                            value={filter.source} 
                            onChange={(e) => { setFilter((prev) => ({...prev, source : Number(e.target.value)})); FilterLibaryContents(filter.search, Number(e.target.value)); }} 
                        >
                            {
                                sourcesOptions.map((item, index) => (
                                    <option value={index} key={index} >{item}</option>
                                ))
                            }
                        </Input>
                    </FormGroup>
                </Col>
            </Row>

            <div className="bg-white">
                <Table responsive bordered >
                    <colgroup>
                        {
                            haveActions ? 
                            <>
                                <col width="40%"></col>
                                <col width="40%"></col>
                                <col width="20%"></col>
                            </>
                            :
                            <>
                                <col width="50%"></col>
                                <col width="50%"></col>
                            </>
                        }
                    </colgroup>
                    <thead>
                        <tr>
                            <th>Image</th>
                            <th>Name & Description</th>
                            {
                                haveActions &&
                                <th>Action</th>
                            }
                        </tr>
                    </thead>
                    <tbody>
                        {
                            (loading === true || refetching === true) &&
                            <tr>
                                <td colSpan={rowLength}>
                                    <Loading />
                                </td>
                            </tr> 
                        }
                        {
                            loading === false && refetching === false && libraryContent.docs.length === 0 && 
                            <tr>
                                <td colSpan={rowLength} className="text-center">No Contents Found</td>
                            </tr>
                        }
                        {
                            loading === false && refetching === false && libraryContent.docs.map((item, index) => (
                                <tr key={index}>
                                    <td className="text-center">
                                        <ToggleableImage 
                                            image={item.video_id}
                                            fromVideo={true}
                                        />
                                    </td>
                                    <td>
                                        <div title={item.name}>
                                            <b className='table-description-details'>{item?.name}</b>
                                        </div>
                                        <div className="table-description-details-long" title={item.description}>{TruncateText(item?.description, 100)}</div>
                                    </td>
                                    {
                                        haveActions &&
                                        <td className="align-middle">
                                            <Button size="sm" color={selectedValue === item.video_id ? "primary" : "secondary"} onClick={() => SelectVideo(item.video_id)}>Select</Button>
                                        </td>
                                    }
                                </tr>
                            ))
                        }
                    </tbody>
                </Table>
            </div>
            <div className="d-flex justify-content-end pt-3">
                <PaginationSet 
                    totalPage={libraryContent.total_pages}
                    page={filter.page}
                    onPageChange={OnPageChange}
                />
            </div> 
        </>
    )
}

